import React, { useRef } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from 'store/store';
import { Button, TextField, Typography } from 'design-component';
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'design-component/Dialog';
import { IconCopy, IconPen, IconTrash, IconWarning, IconCheck } from 'assets/icon';
import { palette } from 'styles/color';
import PageIntro from 'components/PageIntro';
import Table from 'components/Table';
import TooltipMenu from 'components/TooltipMenu';
import { userInfoSelector } from 'store/slices/userInfo';
import { apiWrapper } from 'api';
import { useTranslation } from 'react-i18next';
import { activeToast } from 'store/slices/toastSlice';
import { getApiKeyList, createApiKey, deleteApiKey, updateApiKey } from 'api/api';

const ApiKeys = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { isLogged } = useAppSelector(userInfoSelector);
  const [isOpen, setIsOpen] = React.useState(false);
  const [dialogType, setDialogType] = React.useState<'create' | 'edit'>('create');
  const [isOpenDelete, setIsOpenDelete] = React.useState(false);
  const [name, setName] = React.useState('');
  const [apiKey, setApiKey] = React.useState('');
  const [uuid, setUuid] = React.useState('');
  const [selectedRow, setSelectedRow] = React.useState<number | null>(null);

  const handleClose = () => {
    setIsOpen(false);
    setName('');
    setApiKey('');
    setUuid('');
    setSelectedRow(null);

    queryClient.invalidateQueries({
      queryKey: ['api-key-list']
    });
    // setErrorInfo({ name: '' });
  };

  const COLUMNS = [
    { key: 'apikey_name', title: `${t('name')}` },
    { key: 'apikey', title: `${t('key')}` },
    { key: 'created_time', title: `${t('created')}` },
    { key: 'tooltip', title: '' }
  ];

  // 키 목록 조회
  const { data: keyList } = useQuery({
    queryKey: ['api-key-list'],
    queryFn: getApiKeyList
  });

  // 키 생성
  const { mutate: fetchNewKey } = useMutation({
    mutationKey: ['create-key'],
    mutationFn: createApiKey,
    onSuccess: (res) => {
      const { apikey_name, apikey, uuid } = res;
      setName(apikey_name);
      setApiKey(apikey);
      setUuid(uuid);

      // queryClient.invalidateQueries({
      //   queryKey: ['api-key-list']
      // });
    }
  });

  React.useEffect(() => {
    if (isOpen && dialogType === 'create') {
      fetchNewKey();
    }
  }, [isOpen, dialogType]);

  // 키 삭제
  const { mutate: deleteKey } = useMutation({
    mutationKey: ['delete-key'],
    mutationFn: () => deleteApiKey(selectedRow, keyList),
    onSuccess: () => {
      setSelectedRow(null);
      setIsOpenDelete(false);
      queryClient.invalidateQueries({
        queryKey: ['api-key-list']
      });

      dispatch(
        activeToast({
          type: 'info',
          msg: t('toast.keyDeleted')
        })
      );
    }
  });

  // 키 수정
  const { mutate: updateKey } = useMutation({
    mutationKey: ['update-key'],
    mutationFn: ({ uuid, newName }: { uuid: string; newName: string }) =>
      updateApiKey(uuid, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['api-key-list']
      });
      handleClose();
    }
  });

  const handleUpdate = ({ _id, _name }: { _id: string; _name: string }) => {
    // 이름이 변경된 경우에만 업데이트
    if (_name !== keyList![selectedRow as number]?.apikey_name) {
      updateKey({ uuid: _id, newName: _name });
    } else {
      handleClose();
    }
  };

  return (
    <Container $isLogin={isLogged}>
      {isLogged ? (
        <>
          {/* <div
            style={{
              // 스크롤 시 고정
              position: 'sticky',
              top: 0,
              zIndex: 1,
              background: palette.white
            }}> */}
          <PageIntro title="API Keys" description={t('apikeysDesc')} />
          <Button
            color="primary"
            onClick={() => {
              setDialogType('create');
              setIsOpen(true);
            }}
            style={{
              margin: '14px 0 20px'
            }}
            disabled={keyList && keyList.length >= 2}>
            {t('createNewKey')}
          </Button>
          {/* </div> */}
          {keyList && keyList.length > 0 && (
            <Table columns={COLUMNS} data={keyList} setSelectedRow={setSelectedRow}>
              <TooltipMenu.MenuItem
                onClick={() => {
                  setDialogType('edit');
                  setIsOpen(true);
                  setName(keyList[selectedRow as number].apikey_name);
                  // setApiKey(keyList[selectedRow as number].apikey);
                  setUuid(keyList[selectedRow as number].uuid);
                }}>
                <Button
                  variant="text"
                  color="default"
                  startIcon={<IconPen />}
                  className="item"
                  style={{
                    minWidth: '128px'
                  }}>
                  {t('rename')}
                </Button>
              </TooltipMenu.MenuItem>
              <TooltipMenu.MenuItem
                onClick={() => {
                  setIsOpenDelete(true);
                }}>
                <Button
                  variant="text"
                  color="default"
                  startIcon={<IconTrash />}
                  className="item"
                  style={{
                    minWidth: '128px'
                  }}>
                  {t('delete')}
                </Button>
              </TooltipMenu.MenuItem>
            </Table>
          )}
        </>
      ) : (
        <NotLogin />
      )}

      {/* Dialog */}
      <CreateKeyDialog
        isOpen={isOpen}
        name={name}
        apiKey={apiKey}
        uuid={uuid}
        setName={setName}
        dialogType={dialogType}
        handleClose={handleClose}
        handleUpdate={handleUpdate}
      />
      <DeleteKeyDialog
        isOpenDelete={isOpenDelete}
        setIsOpenDelete={setIsOpenDelete}
        handleDelete={deleteKey}
      />
    </Container>
  );
};

export default ApiKeys;

const CreateKeyDialog = ({
  isOpen,
  name,
  setName,
  apiKey,
  uuid,
  dialogType,
  handleClose,
  handleUpdate
}: {
  isOpen: boolean;
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
  apiKey: string;
  uuid: string;
  dialogType: 'create' | 'edit';
  handleClose: () => void;
  handleUpdate: ({ _id, _name }: { _id: string; _name: string }) => void;
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  // const mouseDownTargetRef = useRef<EventTarget | null>(null); // 마우스 다운 위치

  // useEffect(() => {
  //   const handleMouseDown = (event: MouseEvent) => {
  //     mouseDownTargetRef.current = event.target;
  //   };

  //   const handleMouseUp = (event: MouseEvent) => {
  //     const mouseUpTarget = event.target; // 마우스 업 위치
  //     const mouseDownTarget = mouseDownTargetRef.current;
  //     const dialogElement = dialogRef.current;

  //     if (
  //       dialogElement &&
  //       !dialogElement.contains(mouseDownTarget as Node) &&
  //       !dialogElement.contains(mouseUpTarget as Node)
  //     ) {
  //       handleClose();
  //     }
  //   };

  //   if (isOpen) {
  //     document.addEventListener('mousedown', handleMouseDown);
  //     document.addEventListener('mouseup', handleMouseUp);
  //   }

  //   return () => {
  //     document.removeEventListener('mousedown', handleMouseDown);
  //     document.removeEventListener('mouseup', handleMouseUp);
  //   };
  // }, [isOpen, handleClose]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const regex = /^[a-zA-Z0-9-_ ]*$/; // 허용 문자 : 영문 대소문자, 숫자, -, _, 공백
    if (regex.test(value)) {
      setName(value);
    } else {
      setName((prev) => prev.replace(/[^a-zA-Z0-9-_ ]/g, ''));
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={() => {}}>
      {/* <div ref={dialogRef}> */}
      <DialogTitle>
        {dialogType === 'create' ? `${t('createNewKey')}` : `${t('renameKey')}`}
      </DialogTitle>
      <DialogContent>
        <TextField
          id="name"
          label={`${t('name')}`}
          value={name}
          onChange={handleNameChange}
          placeholder={`${t('namePlaceholder')}`}
        />
        {dialogType === 'create' && (
          <>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-end',
                margin: '40px 0'
              }}>
              <TextField
                id="key"
                label={`${t('key')}`}
                readOnly
                value={apiKey}
                style={{
                  flex: 1
                }}
              />
              <CopyButton textToCopy={apiKey} />
            </div>
            <Typography size="12px" weight={400} height="150%" color={palette.gray[500]}>
              {t('createNewKeyDesc')}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {dialogType === 'edit' && (
          <Button
            color="primary"
            onClick={handleClose}
            style={{
              minWidth: '170px'
            }}>
            {t('cancel')}
          </Button>
        )}
        <Button
          color="primary"
          disabled={!name.trim().length}
          onClick={() => {
            handleUpdate({ _id: uuid, _name: name });
            handleClose();
          }}
          style={{
            minWidth: '170px',
            background: palette.blue[500],
            color: palette.white
          }}>
          {t('confirm')}
        </Button>
      </DialogActions>
      {/* </div> */}
    </Dialog>
  );
};

const DeleteKeyDialog = ({
  isOpenDelete,
  setIsOpenDelete,
  handleDelete
}: {
  isOpenDelete: boolean;
  setIsOpenDelete: React.Dispatch<React.SetStateAction<boolean>>;
  handleDelete: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Dialog isOpen={isOpenDelete} onClose={() => setIsOpenDelete(false)}>
      <DialogTitle
        style={{
          paddingBottom: '6px'
        }}>
        {t('deleteKey')}
      </DialogTitle>
      <DialogContent>
        <Callout>
          <Typography color={palette.red}>
            <IconWarning />
          </Typography>

          <Typography component="p" size="14px" weight={500} height="150%" color={palette.red}>
            {t('deleteKeyDesc')}
          </Typography>
        </Callout>
      </DialogContent>
      <DialogActions>
        <Button
          color="error"
          onClick={handleDelete}
          style={{
            minWidth: '170px',
            background: palette.red,
            color: palette.white
          }}>
          {t('delete')}
        </Button>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => setIsOpenDelete(false)}
          style={{
            minWidth: '170px'
          }}>
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CopyButton = ({ textToCopy }: { textToCopy: string }) => {
  const [isCopied, setIsCopied] = React.useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      dispatch(
        activeToast({
          type: 'info',
          msg: `${t('toast.copyApikeySuccess')}`
        })
      );
    } catch (err) {
      dispatch(
        activeToast({
          type: 'error',
          msg: `${t('toast.copyApikeyFail')}`
        })
      );
    }
  };

  React.useEffect(() => {
    const handleMouseUp = () => {
      setIsCopied(false);
    };

    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <Button
      color="primary"
      onMouseDown={handleCopy}
      startIcon={isCopied ? <IconCheck /> : <IconCopy />}
      style={{
        minWidth: '78px'
      }}>
      {t('copy')}
    </Button>
  );
};

const NotLogin = () => {
  const { t } = useTranslation();
  // const dispatch = useAppDispatch();
  // const handleLogin = () => {
  //   dispatch(login({ userName: 'User_01', email: 'user01@polarisoffice.com' }));
  // };

  const handleLogin = () => apiWrapper(`/api/v1/datainsight/playground/userinfo`);

  return (
    <div
      style={{
        width: '242px'
      }}>
      <Typography size="22px" weight={700} height="100%" color={palette.black} align="center">
        API Keys
      </Typography>
      <Typography
        size="16px"
        weight={400}
        height="25px"
        color={palette.gray[500]}
        align="center"
        style={{
          margin: '8px 0 20px'
        }}>
        {t('loginDesc')}
      </Typography>
      <Button variant="outlined" color="primary" fullWidth onClick={handleLogin}>
        {t('login')}
      </Button>
    </div>
  );
};

// const ButtonDefault = styled(Button)`
//   background: ${palette.white};
//   color: ${palette.gray[900]};
//   border: 1px solid ${palette.gray[300]};
//   &:hover {
//     background: ${palette.white};
//     color: ${palette.gray[900]};
//     border: 1px solid ${palette.gray[300]};
//   }
// `;

const Container = styled.div<{ $isLogin?: boolean }>`
  ${({ $isLogin }) =>
    !$isLogin && {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%'
    }}
`;

const Callout = styled.div`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 13px;
  background: #f010001a;
  border-radius: 8px;
`;
